import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  GlobalStyles,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import moment from "moment";
import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useGetBranches } from "src/api/generic";
import {
  useGetOCReport,
  useGetOperationCostReportData,
  type OperationCostReportDataPayload,
  type OperationCostReportRow,
} from "src/api/reports";
import { REPORT_BRANCH_MULTISELECT_ALL_SENTINEL } from "src/constants/reportBranchSelect";
import DateRangeInput from "src/shared/components/date-range";
import {
  ReportControlBar,
  ReportPageHeading,
  ReportSectionCard,
} from "src/shared/components/report-layout";
import { authState } from "src/store/auth";
import RightArrow from "src/assets/svg-icons/aggregators/right_arrow.svg";
import {
  downloadAsXLS,
  oneBranchHasPermission,
  specificBranchHasPermission,
} from "src/utils";
import { ReportsPermissions } from "src/constants/permissions";

type OcReportFilters = {
  startDate: string;
  endDate: string;
  branchIds: number[];
};

const parseOcReportFilters = (sp: URLSearchParams): OcReportFilters => {
  const b = sp.getAll("branches");
  return {
    startDate:
      sp.get("start_date") || moment().startOf("month").format("yyyy-MM-DD"),
    endDate:
      sp.get("end_date") || moment().endOf("month").format("yyyy-MM-DD"),
    branchIds: b.length ? b.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : [],
  };
};

const formatBills = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

type OcTypeFilterKey = "" | OperationCostReportRow["type_key"];

const OperationExpensesReportPage = () => {
  const { t } = useTranslation("reports");
  const { t: generalT } = useTranslation("general");
  const { t: ocT } = useTranslation("operation-cost");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useRecoilValue(authState);
  const companyName = auth.user?.company?.name;

  const [appliedFilters, setAppliedFilters] = useState<OcReportFilters>(() =>
    parseOcReportFilters(new URLSearchParams(searchParams.toString()))
  );
  const [draftFilters, setDraftFilters] = useState<OcReportFilters>(() =>
    parseOcReportFilters(new URLSearchParams(searchParams.toString()))
  );

  const [appliedExpenseName, setAppliedExpenseName] = useState("");
  const [appliedTypeKey, setAppliedTypeKey] = useState<OcTypeFilterKey>("");
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [modalExpenseDraft, setModalExpenseDraft] = useState("");
  const [modalTypeDraft, setModalTypeDraft] = useState<OcTypeFilterKey>("");

  const { startDate, endDate, branchIds } = appliedFilters;

  const { data: branchesData } = useGetBranches();
  const branchesHavePermission =
    branchesData?.data.filter((br) =>
      specificBranchHasPermission(
        ReportsPermissions.viewThirdEyeReportOperationExpenses,
        br.id
      )
    ) ?? [];

  const { data: reportBody, isFetching, refetch } = useGetOperationCostReportData({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: true,
  });

  const { refetch: refetchDownload, isFetching: isDownloadLoading } = useGetOCReport({
    start_date: startDate,
    end_date: endDate,
    branches: branchIds.length > 0 ? branchIds : undefined,
    enabled: false,
  });

  const downloadReportsPermission = oneBranchHasPermission(
    ReportsPermissions.viewThirdEyeReportOperationExpenses
  );

  const payload = (reportBody as { data?: OperationCostReportDataPayload } | null | undefined)
    ?.data;

  const totalOc = payload?.total_oc ?? 0;

  const expenseOptions = useMemo(() => {
    const rows = payload?.rows ?? [];
    const names = new Set<string>();
    rows.forEach((r) => {
      const n = r.expense?.trim();
      if (n) names.add(n);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [payload?.rows]);

  const filteredRows = useMemo(() => {
    const dataRows = payload?.rows ?? [];
    const exp = appliedExpenseName.trim();
    const tk = appliedTypeKey;
    return dataRows.filter((r) => {
      if (r.amount === 0) return false;
      if (exp && r.expense.trim() !== exp) return false;
      if (tk && r.type_key !== tk) return false;
      return true;
    });
  }, [payload?.rows, appliedExpenseName, appliedTypeKey]);

  const periodLabel = useMemo(
    () =>
      t("reportDateRange", {
        start: moment(startDate).format("D MMM YYYY"),
        end: moment(endDate).format("D MMM YYYY"),
      }),
    [t, startDate, endDate]
  );

  const openFilterModal = () => {
    setModalExpenseDraft(appliedExpenseName);
    setModalTypeDraft(appliedTypeKey);
    setFilterModalOpen(true);
  };

  const cancelFilterModal = () => setFilterModalOpen(false);

  const commitFiltersFromModal = () => {
    setAppliedExpenseName(modalExpenseDraft.trim());
    setAppliedTypeKey(modalTypeDraft);
    setFilterModalOpen(false);
  };

  const clearFiltersInModal = () => {
    setModalExpenseDraft("");
    setModalTypeDraft("");
    setAppliedExpenseName("");
    setAppliedTypeKey("");
    setFilterModalOpen(false);
  };

  const syncAppliedToUrl = useCallback(
    (next: OcReportFilters) => {
      const p = new URLSearchParams();
      p.set("start_date", next.startDate);
      p.set("end_date", next.endDate);
      next.branchIds.forEach((id) => p.append("branches", String(id)));
      setSearchParams(p);
    },
    [setSearchParams]
  );

  const handleReportUpdate = () => {
    setAppliedFilters({ ...draftFilters });
    syncAppliedToUrl(draftFilters);
    void refetch();
  };

  const handlePrint = () => window.print();

  const handleDownload = useCallback(async () => {
    const result = await refetchDownload();
    const d = result.data as string | undefined;
    if (d != null) {
      downloadAsXLS(`\uFEFF${d}`, "operation-cost-report");
    }
  }, [refetchDownload]);

  const footerBillsAmount = useMemo(() => {
    const exp = appliedExpenseName.trim();
    const tk = appliedTypeKey;
    if (!exp && !tk) return totalOc;
    return filteredRows.reduce((s, r) => s + r.amount, 0);
  }, [appliedExpenseName, appliedTypeKey, totalOc, filteredRows]);

  const ocTypeLabel = (key: OperationCostReportRow["type_key"]) => {
    if (key === "fixed") return generalT("fixed");
    if (key === "asset") return ocT("asset");
    return ocT("variable");
  };

  const branchSelectControl = (
    value: OcReportFilters["branchIds"],
    onChange: (ids: number[]) => void
  ) => (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel id="oc-report-branches" shrink>
        {generalT("branch")}
      </InputLabel>
      <Select<string[]>
        labelId="oc-report-branches"
        multiple
        value={value.map((id) => String(id))}
        onChange={(e: SelectChangeEvent<string[]>) => {
          const v = e.target.value;
          const next = typeof v === "string" ? v.split(",") : v;
          const strs = next as string[];
          if (strs.includes(REPORT_BRANCH_MULTISELECT_ALL_SENTINEL)) {
            onChange([]);
            return;
          }
          const ids = strs.map(Number).filter((n) => n > 0);
          onChange(ids);
        }}
        input={<OutlinedInput label={generalT("branch")} />}
        renderValue={(selected) => {
          if (selected.length === 0) return t("allBranches");
          return selected
            .map(
              (idStr) =>
                branchesHavePermission.find((b) => String(b.id) === idStr)?.name ?? idStr
            )
            .join(", ");
        }}
      >
        <MenuItem
          key="__all-branches-oc"
          value={REPORT_BRANCH_MULTISELECT_ALL_SENTINEL}
          sx={{
            borderBottom: "1px solid #EAECF0",
            bgcolor: "#F9FAFB",
          }}
        >
          <Typography fontWeight={600} variant="body2" component="span">
            {t("allBranches")}
          </Typography>
        </MenuItem>
        {branchesHavePermission.map((b) => (
          <MenuItem key={b.id} value={String(b.id)}>
            <Checkbox size="small" checked={value.includes(b.id)} />
            <ListItemText primary={b.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const dateRangeControl = (
    draft: OcReportFilters,
    setDraft: Dispatch<SetStateAction<OcReportFilters>>
  ) => (
    <Box sx={{ minWidth: 250 }}>
      <DateRangeInput
        startDate={draft.startDate ? moment(draft.startDate) : null}
        startDateId="oc-report-start"
        endDate={draft.endDate ? moment(draft.endDate) : null}
        endDateId="oc-report-end"
        onDatesChange={(arg: {
          startDate: moment.Moment | null;
          endDate: moment.Moment | null;
        }) => {
          const s = arg.startDate?.format("yyyy-MM-DD");
          const e = arg.endDate?.format("yyyy-MM-DD");
          if (s && e) {
            setDraft((prev) => ({ ...prev, startDate: s, endDate: e }));
          }
        }}
      />
    </Box>
  );

  return (
    <Box p={{ xs: 2, sm: "12px 32px 32px" }} maxWidth="100%">
      <GlobalStyles
        styles={{
          "@media print": {
            "body *": { visibility: "hidden" },
            "#oc-report-print-root, #oc-report-print-root *": {
              visibility: "visible",
            },
            "#oc-report-print-root": {
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
            },
          },
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        mb={1.5}
        sx={{ "@media print": { display: "none" } }}
      >
        <Typography
          fontSize="0.9rem"
          color="#475467"
          onClick={() => navigate("/reports")}
          sx={{ cursor: "pointer" }}
        >
          {t("thirdEyeReport")}
        </Typography>
        <img src={RightArrow} alt="" style={{ width: 12, height: 12 }} />
        <Box borderRadius="4px" bgcolor="#EAECF0" px={1} py={0.5}>
          <Typography color="#344054" fontWeight={600} fontSize="0.85rem">
            {t("operationExpensesReport")}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ "@media print": { display: "none" } }} mb={2}>
        <ReportControlBar
          left={
            <>
              {branchSelectControl(draftFilters.branchIds, (ids) =>
                setDraftFilters((p) => ({ ...p, branchIds: ids }))
              )}
              {dateRangeControl(draftFilters, setDraftFilters)}
            </>
          }
          center={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<FilterListIcon fontSize="small" />}
                onClick={openFilterModal}
                sx={{
                  height: 40,
                  minWidth: 100,
                  borderColor: "#D0D5DD",
                  color: "#344054",
                }}
              >
                {t("filter")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon fontSize="small" />}
                onClick={handleReportUpdate}
                disabled={isFetching}
                sx={{ height: 40, minWidth: 120 }}
              >
                {t("reportUpdate")}
              </Button>
            </>
          }
          right={
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<PrintIcon fontSize="small" />}
                onClick={handlePrint}
                sx={{ height: 40, minWidth: 100, borderColor: "#D0D5DD", color: "#344054" }}
              >
                {t("print")}
              </Button>
              <LoadingButton
                variant="outlined"
                startIcon={<FileDownloadIcon fontSize="small" />}
                onClick={handleDownload}
                loading={isDownloadLoading}
                disabled={!downloadReportsPermission}
                sx={{ height: 40, minWidth: 100, borderColor: "#D0D5DD", color: "#344054" }}
              >
                {t("export")}
              </LoadingButton>
            </>
          }
        />
      </Box>

      <Dialog open={filterModalOpen} onClose={cancelFilterModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}>
          <FilterListIcon sx={{ color: "#475467", fontSize: 22 }} />
          <Typography component="span" fontWeight={700} flex={1}>
            {t("filter")}
          </Typography>
          <IconButton aria-label="close" onClick={cancelFilterModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="oc-modal-expense" shrink>
                {t("filterModalExpenseName")}
              </InputLabel>
              <Select<string>
                labelId="oc-modal-expense"
                label={t("filterModalExpenseName")}
                value={modalExpenseDraft}
                displayEmpty
                onChange={(e) =>
                  setModalExpenseDraft(typeof e.target.value === "string" ? e.target.value : "")
                }
                input={<OutlinedInput notched label={t("filterModalExpenseName")} />}
                renderValue={(selected) =>
                  selected ? selected : generalT("all")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{generalT("all")}</em>
                </MenuItem>
                {expenseOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    <Box component="span" dir="auto">
                      {name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="oc-modal-type" shrink>
                {ocT("type")}
              </InputLabel>
              <Select<OcTypeFilterKey>
                labelId="oc-modal-type"
                label={ocT("type")}
                value={modalTypeDraft}
                displayEmpty
                onChange={(e) =>
                  setModalTypeDraft((e.target.value || "") as OcTypeFilterKey)
                }
                input={<OutlinedInput notched label={ocT("type")} />}
                renderValue={(selected) =>
                  selected ? ocTypeLabel(selected) : generalT("all")
                }
                sx={{
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
              >
                <MenuItem value="">
                  <em>{generalT("all")}</em>
                </MenuItem>
                <MenuItem value="fixed">{generalT("fixed")}</MenuItem>
                <MenuItem value="asset">{ocT("asset")}</MenuItem>
                <MenuItem value="variable">{ocT("variable")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexDirection: "column", gap: 1 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={commitFiltersFromModal}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {t("applyFilters")}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={clearFiltersInModal}
            sx={{ textTransform: "none", borderColor: "#D0D5DD", color: "#344054" }}
          >
            {generalT("clear")}
          </Button>
          <Button color="inherit" fullWidth onClick={cancelFilterModal} sx={{ textTransform: "none" }}>
            {generalT("cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box id="oc-report-print-root">
        <ReportPageHeading
          title={t("operationExpensesReport")}
          subtitle={companyName}
          periodLabel={periodLabel}
        />

        <ReportSectionCard>
          {isFetching && !payload ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxWidth: "100%",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                stickyHeader
                sx={{ minWidth: 480 }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        bgcolor: "#F2F4F7",
                        color: "#344054",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        borderBottom: "1px solid #EAECF0",
                        py: 1.5,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="left">{t("expenseColumn")}</TableCell>
                    <TableCell align="right">{t("billsColumn")}</TableCell>
                    <TableCell align="right">{t("ocTypeColumn")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 && !isFetching ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">{generalT("noData")}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row, i) => (
                      <TableRow
                        key={`${row.expense}-${i}`}
                        sx={{
                          "& td": {
                            borderColor: "#EEF0F2",
                            fontSize: "0.875rem",
                            color: "#101828",
                            py: 1.25,
                          },
                        }}
                      >
                        <TableCell align="left" sx={{ fontWeight: 500 }}>
                          <Box component="span" dir="auto">
                            {row.expense}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500 }}>
                          {formatBills(row.amount)}
                        </TableCell>
                        <TableCell align="right" color="text.secondary" sx={{ fontWeight: 500 }}>
                          <Box component="span" dir="auto">
                            {row.type}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
                {payload && (payload.rows ?? []).length > 0 && (
                  <TableFooter
                    sx={{
                      "& tr": {
                        backgroundColor: "#F9FAFB",
                        borderTop: "2px solid",
                        borderColor: "primary.main",
                        "& td": {
                          color: "#101828",
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          py: 1.5,
                          borderBottom: "none",
                        },
                        "& td:first-of-type": {
                          borderBottomLeftRadius: 8,
                        },
                        "& td:last-of-type": {
                          borderBottomRightRadius: 8,
                        },
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell align="left" component="th" scope="row">
                        {t("totalOcLabel")}
                      </TableCell>
                      <TableCell align="right" component="td">
                        {formatBills(footerBillsAmount)}
                      </TableCell>
                      <TableCell align="right" component="td" />
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </TableContainer>
          )}
        </ReportSectionCard>
      </Box>
    </Box>
  );
};

export default OperationExpensesReportPage;
